import { useState, useMemo, useCallback, useEffect } from 'react';
import { PaymentMethod } from '@packlink/packlink-sdk';
import { AmplitudeProperties } from '@packlink/metrics';
import { useTranslation } from '@packlink/translation-provider';
import { initLocalPayment } from '../../../utils/braintree.js';
import { jsx } from '@emotion/react/jsx-runtime';
import { LocalPayment } from '../../PaymentComponents/LocalPayment/LocalPayment.js';
import { LoggerPaymentErrorTagValues } from '../../../types/types.js';

var BREADCRUMBS = /*#__PURE__*/function (BREADCRUMBS) {
  BREADCRUMBS["SETUP_SUCCESS"] = "Local Payment setup success";
  BREADCRUMBS["SETUP_ERROR"] = "Local Payment setup error";
  BREADCRUMBS["VERIFICATION_STARTS"] = "Local Payment verification starts";
  BREADCRUMBS["VERIFICATION_SUCCESS"] = "Local Payment verification success";
  BREADCRUMBS["HAS_TOKENIZATION_PARAMS"] = "Local Payment has tokenization params";
  return BREADCRUMBS;
}(BREADCRUMBS || {});
const PaymentOptionLocal = _ref => {
  let {
    amount,
    cdnUrl,
    clientBraintree,
    currency,
    fallbackOptions,
    isPaymentBlocked = false,
    isProcessing,
    type,
    onCheckForExternalError,
    onSetupSuccess,
    onSetupError,
    onVerificationStart,
    onVerificationSuccess,
    onVerificationError,
    onHasTokenizationParams
  } = _ref;
  const {
    t
  } = useTranslation();
  const [isReady, setIsReady] = useState(false);
  const [isValidatingBraintree, setIsValidatingBraintree] = useState(false);
  const [localPaymentInstance, setLocalPaymentInstance] = useState();
  const [isTokenizationPayment, setIsTokenizationPayment] = useState(false);
  const isButtonLoading = useMemo(() => !isReady || isValidatingBraintree || !!isProcessing, [isReady, isValidatingBraintree, isProcessing]);
  const onLocalPaymentVerificationError = useCallback(_ref2 => {
    let {
      message
    } = _ref2;
    setIsValidatingBraintree(false);
    onVerificationError(type, message, LoggerPaymentErrorTagValues.PAYMENT_LOCAL_PAYMENT_VERIFICATION);
  }, [onVerificationError, type]);
  const onLocalPaymentVerificationSuccess = useCallback(payload => {
    const paymentMetrics = {
      [AmplitudeProperties.PAYMENT_METHOD]: type
    };
    setIsValidatingBraintree(false);
    onVerificationSuccess(type, BREADCRUMBS.VERIFICATION_SUCCESS, payload, paymentMetrics);
  }, [type, onVerificationSuccess]);
  const onLocalPaymentVerificationStart = useCallback((_data, continuePayment) => {
    setIsValidatingBraintree(true);
    onVerificationStart(BREADCRUMBS.VERIFICATION_STARTS);
    continuePayment === null || continuePayment === void 0 ? void 0 : continuePayment();
  }, [onVerificationStart]);
  const onLocalPaymentHasTokenizationParams = useCallback(() => {
    setIsValidatingBraintree(true);
    onHasTokenizationParams === null || onHasTokenizationParams === void 0 ? void 0 : onHasTokenizationParams(BREADCRUMBS.HAS_TOKENIZATION_PARAMS);
  }, [onHasTokenizationParams]);
  const startPayment = useCallback(() => {
    if (isPaymentBlocked) {
      onCheckForExternalError === null || onCheckForExternalError === void 0 ? void 0 : onCheckForExternalError();
      return;
    } else if (!isPaymentBlocked && localPaymentInstance && currency) {
      localPaymentInstance.startPayment({
        paymentType: type.toLowerCase(),
        amount,
        fallback: {
          url: (fallbackOptions === null || fallbackOptions === void 0 ? void 0 : fallbackOptions.url) || window.location.href,
          buttonText: t('payment.local-payment.fallback-button', {
            pageTitle: (fallbackOptions === null || fallbackOptions === void 0 ? void 0 : fallbackOptions.buttonText) || document.title
          })
        },
        currencyCode: currency,
        onPaymentStart: onLocalPaymentVerificationStart
      }, (paymentError, payload) => {
        if (paymentError) {
          onLocalPaymentVerificationError(paymentError);
        } else {
          onLocalPaymentVerificationSuccess(payload);
        }
      });
    }
  }, [localPaymentInstance, amount, currency, type, fallbackOptions === null || fallbackOptions === void 0 ? void 0 : fallbackOptions.url, fallbackOptions === null || fallbackOptions === void 0 ? void 0 : fallbackOptions.buttonText, t, onLocalPaymentVerificationStart, onLocalPaymentVerificationError, onLocalPaymentVerificationSuccess]);
  useEffect(() => {
    /**
     * Here we handle the case when the user returns from paying in the bank application
     * and the URL presents all the necessary query params
     */
    if (!isTokenizationPayment && localPaymentInstance) {
      if (localPaymentInstance.hasTokenizationParams()) {
        setIsTokenizationPayment(true);
        onLocalPaymentHasTokenizationParams();
        onLocalPaymentVerificationStart();
        localPaymentInstance.tokenize({}, (error, payload) => {
          if (error) {
            onLocalPaymentVerificationError(error);
          } else {
            onLocalPaymentVerificationSuccess(payload);
          }
        });
      }
    }
  }, [localPaymentInstance, onHasTokenizationParams, onVerificationError, onVerificationSuccess, onVerificationStart, isTokenizationPayment, onLocalPaymentVerificationStart, onLocalPaymentVerificationError, onLocalPaymentVerificationSuccess, onLocalPaymentHasTokenizationParams]);
  useEffect(() => {
    const initializeBraintree = async client => {
      try {
        const localPayment = await initLocalPayment(client);
        setLocalPaymentInstance(localPayment);
        onSetupSuccess(BREADCRUMBS.SETUP_SUCCESS);
        setIsReady(true);
      } catch (error) {
        onSetupError(error, BREADCRUMBS.SETUP_ERROR, LoggerPaymentErrorTagValues.PAYMENT_PAYPAL_SETUP, PaymentMethod.PAYPAL);
        setIsReady(false);
      }
    };
    clientBraintree && !localPaymentInstance && initializeBraintree(clientBraintree);
  }, [clientBraintree, localPaymentInstance, onSetupError, onSetupSuccess]);
  return jsx(LocalPayment, {
    type: type,
    cdnUrl: cdnUrl,
    buttonLoading: isButtonLoading,
    ariaLabelText: t('payment.local-payment.label', {
      type: type.toLowerCase()
    }),
    onPay: startPayment
  });
};

export { PaymentOptionLocal };
//# sourceMappingURL=PaymentOptionLocal.js.map
